import React, { createElement, useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FiLayers } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from "@core/types/errorResponse";
import { fetchAllPages } from "@core/utils/fetchAllPages";
import { SORT_BY_SUB_GROUP_NAME, SORT_DIR_ASC } from "@core/constants/listSort";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { ReportTemplate } from "@features/report/template/types/reportTemplate";
import {
    assignTemplateReportSubGroupsAsync,
    fetchNationReportSubGroupsAsync,
    fetchTemplateReportSubGroupsAsync,
    removeTemplateReportSubGroupsAsync,
} from "@features/report/subgroup/services/reportSubGroupSvc";
import { ReportSubGroup } from "@features/report/subgroup/types/reportSubGroup";
import AssignModalShell from "@core/components/assign/AssignModalShell";
import AssignDualListPanels from "@core/components/assign/AssignDualListPanels";
import AssignAvailableRow from "@core/components/assign/AssignAvailableRow";
import AssignAssignedRow from "@core/components/assign/AssignAssignedRow";
import {
    buildActiveLookup,
    filterActiveForAssignPayload,
    resolveAssignedRowDisplay,
    sortAttachedByOrder,
} from "@core/components/assign/assignDisplay";
import { renumber, setItemOrder } from "@core/components/assign/assignListOrder";

type Props = {
    show: boolean;
    onHide: () => void;
    reportTemplate: ReportTemplate | null;
    onSaved?: () => void;
};

type AttachedSubGroup = {
    report_sub_group_id: number;
    list_order: number;
    sub_group_name?: string;
    sub_group_description?: string | null;
};

const AssignTemplateSubGroupModal: React.FC<Props> = ({
    show,
    onHide,
    reportTemplate,
    onSaved,
}) => {
    const [searchParams] = useSearchParams();
    const resolvedDepartmentId = Number(searchParams.get("did"));
    const resolvedNationId = useScopeId() ?? 0;

    const [allSubGroups, setAllSubGroups] = useState<ReportSubGroup[]>([]);
    const [attached, setAttached] = useState<AttachedSubGroup[]>([]);
    const [initialAttachedIds, setInitialAttachedIds] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [qLeft, setQLeft] = useState("");
    const [qRight, setQRight] = useState("");

    const showError = useApiError();

    const activeSubGroupIds = useMemo(
        () => new Set(allSubGroups.map((sg) => sg.report_sub_group_id)),
        [allSubGroups]
    );
    const activeSubGroupLookup = useMemo(
        () =>
            buildActiveLookup(
                allSubGroups,
                (sg) => sg.report_sub_group_id,
                (sg) => sg.name,
                (sg) => sg.description ?? undefined
            ),
        [allSubGroups]
    );

    useEffect(() => {
        if (!show || !reportTemplate?.report_template_id) return;

        let cancelled = false;

        (async () => {
            setLoading(true);

            try {
                const [allItems, assignedItems] = await Promise.all([
                    fetchAllPages<ReportSubGroup>((page) =>
                        fetchNationReportSubGroupsAsync(resolvedNationId, {
                            all: true,
                            page,
                            isActive: true,
                            sortBy: SORT_BY_SUB_GROUP_NAME,
                            sortDir: SORT_DIR_ASC,
                        })
                    ),
                    fetchAllPages<any>((page) =>
                        fetchTemplateReportSubGroupsAsync(
                            resolvedNationId,
                            resolvedDepartmentId,
                            reportTemplate.report_template_id,
                            { all: true, page }
                        )
                    ),
                ]);

                if (cancelled) return;

                const seen = new Set<number>();
                const uniqueSubGroups = allItems.filter((subGroup) => {
                    if (seen.has(subGroup.report_sub_group_id)) return false;
                    seen.add(subGroup.report_sub_group_id);
                    return true;
                });

                setAllSubGroups(uniqueSubGroups);

                const normalizedAssignments = renumber(
                    assignedItems.map((item: any, index: number) => ({
                        report_sub_group_id: Number(item.report_sub_group_id),
                        list_order: Number(item.list_order ?? index + 1),
                        sub_group_name: item.sub_group_name ?? item.name,
                        sub_group_description: item.sub_group_description ?? item.description ?? null,
                    }))
                );

                setAttached(normalizedAssignments);
                setInitialAttachedIds(
                    normalizedAssignments.map((item) => item.report_sub_group_id)
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        reportTemplate?.report_template_id,
        resolvedDepartmentId,
        resolvedNationId,
        show,
    ]);

    const attachedIds = useMemo(
        () => attached.map((item) => item.report_sub_group_id),
        [attached]
    );

    const left = useMemo(() => {
        const base = allSubGroups.filter(
            (subGroup) => !attachedIds.includes(subGroup.report_sub_group_id)
        );

        if (!qLeft.trim()) return base;

        const search = qLeft.toLowerCase();
        return base.filter((subGroup) =>
            subGroup.name.toLowerCase().includes(search)
        );
    }, [allSubGroups, attachedIds, qLeft]);

    const visibleAttached = useMemo(() => {
        const ordered = sortAttachedByOrder(attached);

        if (!qRight.trim()) return ordered;

        const search = qRight.toLowerCase();
        return ordered.filter((row) => {
            const display = resolveAssignedRowDisplay(
                row.report_sub_group_id,
                activeSubGroupIds,
                activeSubGroupLookup,
                {
                    title: row.sub_group_name ?? `Sub group #${row.report_sub_group_id}`,
                    sublabel: row.sub_group_description ?? undefined,
                }
            );
            return (
                display.title.toLowerCase().includes(search) ||
                (display.sublabel?.toLowerCase().includes(search) ?? false)
            );
        });
    }, [attached, qRight, activeSubGroupIds, activeSubGroupLookup]);

    const attach = (id: number) => {
        const subGroup = allSubGroups.find((sg) => sg.report_sub_group_id === id);
        setAttached((prev) => {
            if (prev.some((item) => item.report_sub_group_id === id)) return prev;
            return renumber([
                ...prev,
                {
                    report_sub_group_id: id,
                    list_order: prev.length + 1,
                    sub_group_name: subGroup?.name,
                    sub_group_description: subGroup?.description ?? null,
                },
            ]);
        });
    };

    const detach = (id: number) => {
        setAttached((prev) =>
            renumber(prev.filter((item) => item.report_sub_group_id !== id))
        );
    };

    const handleOrderChange = (id: number, newOrder: number) => {
        setAttached((prev) =>
            setItemOrder(prev, id, newOrder, (x) => x.report_sub_group_id)
        );
    };

    const handleClose = () => {
        setQLeft("");
        setQRight("");
        onHide();
    };

    const handleSave = async () => {
        if (!reportTemplate?.report_template_id) return;

        setSaving(true);

        try {
            const currentIds = new Set(
                attached.map((item) => item.report_sub_group_id)
            );
            const removedIds = initialAttachedIds.filter(
                (id) => !currentIds.has(id)
            );
            const activeAttached = filterActiveForAssignPayload(
                attached,
                activeSubGroupIds,
                (x) => x.report_sub_group_id
            );

            let successMessage = "Report sub groups updated successfully.";

            if (removedIds.length > 0) {
                const removeResponse = await removeTemplateReportSubGroupsAsync(
                    resolvedNationId,
                    resolvedDepartmentId,
                    reportTemplate.report_template_id,
                    { sub_group_ids: removedIds }
                );

                if (!removeResponse.isSuccess()) {
                    showError(removeResponse.getError() as ErrorResponse);
                    return;
                }

                successMessage =
                    removeResponse.getData()?.message ?? successMessage;
            }

            if (activeAttached.length > 0) {
                const assignResponse = await assignTemplateReportSubGroupsAsync(
                    resolvedNationId,
                    resolvedDepartmentId,
                    reportTemplate.report_template_id,
                    {
                        sub_groups: renumber(activeAttached).map((item) => ({
                            report_sub_group_id: item.report_sub_group_id,
                            list_order: item.list_order,
                        })),
                    }
                );

                if (!assignResponse.isSuccess()) {
                    showError(assignResponse.getError() as ErrorResponse);
                    return;
                }

                successMessage =
                    assignResponse.getData()?.message ?? successMessage;
            } else if (removedIds.length === 0) {
                handleClose();
                return;
            }

            showSuccess(successMessage);
            onSaved?.();
            handleClose();
        } catch (error) {
            showError(error as ErrorResponse);
        } finally {
            setSaving(false);
        }
    };

    return (
        <AssignModalShell
            show={show}
            onHide={handleClose}
            title={
                <>
                    <FiLayers className="me-2" aria-hidden />
                    Manage Sub Groups
                    {reportTemplate ? `: ${reportTemplate.report_template_name}` : ""}
                </>
            }
            saving={saving}
            onSave={handleSave}
            onCancel={handleClose}
            testId="assign-template-sub-group-modal"
            cancelTestId="cancel-sub-group-assignment-button"
            saveTestId="save-sub-group-assignment-button"
        >
            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                </div>
            ) : (
                <AssignDualListPanels
                    availableTitle="Available Sub Groups"
                    assignedTitle="Assigned to Template"
                    availableCount={left.length}
                    assignedCount={attached.length}
                    availableSearchPlaceholder="Search sub groups..."
                    assignedSearchPlaceholder="Search assigned sub groups..."
                    availableSearch={qLeft}
                    assignedSearch={qRight}
                    onAvailableSearchChange={setQLeft}
                    onAssignedSearchChange={setQRight}
                    availableSearchTestId="search-available-sub-groups"
                    assignedSearchTestId="search-assigned-sub-groups"
                    availableEmptyText="No more sub groups to add."
                    assignedEmptyText="No sub groups assigned to this template."
                    availableIsEmpty={left.length === 0}
                    assignedIsEmpty={visibleAttached.length === 0}
                    availableItems={left.map((subGroup) =>
                        createElement(AssignAvailableRow, {
                            key: subGroup.report_sub_group_id,
                            title: subGroup.name,
                            sublabel: subGroup.description ?? undefined,
                            onAdd: () => attach(subGroup.report_sub_group_id),
                            addTestId: `attach-sub-group-button-${subGroup.report_sub_group_id}`,
                        })
                    )}
                    assignedItems={visibleAttached.map((row) => {
                        const display = resolveAssignedRowDisplay(
                            row.report_sub_group_id,
                            activeSubGroupIds,
                            activeSubGroupLookup,
                            {
                                title: row.sub_group_name ?? `Sub group #${row.report_sub_group_id}`,
                                sublabel: row.sub_group_description ?? undefined,
                            }
                        );

                        return createElement(AssignAssignedRow, {
                            key: row.report_sub_group_id,
                            title: display.title,
                            sublabel: display.sublabel,
                            isActive: display.isActive,
                            listOrder: row.list_order,
                            listLength: visibleAttached.length,
                            onOrderChange: (newOrder: number) =>
                                handleOrderChange(row.report_sub_group_id, newOrder),
                            onRemove: () => detach(row.report_sub_group_id),
                            requiredSwitchId: `sub-group-order-${row.report_sub_group_id}`,
                        });
                    })}
                />
            )}
        </AssignModalShell>
    );
};

export default AssignTemplateSubGroupModal;
